import React from 'react';
import { connect } from 'react-redux';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';

import Button from '@components/Button';

import {
  CITIZEN_PORTAL_CONFIG_SAVE_SUCCESS,
  fetchAgencyProductConfigs,
  fetchAgencySettings,
  fetchCitizenPortalConfig,
  saveCitizenPortalConfig,
} from '@actions/agency';

import { validatePortalFooterUrlFields, type PortalFooterUrlField } from '@utils/citizenPortalConfigValidation';

import './style.scss';

const DEFAULT_COLORS = {
  siteBannerColor: '#232056',
  buttonColor: '#232056',
  embeddedFaqTitleColor: '#232056',
  faqCategoryColor: '#5B98DD',
  faqTitleColor: '#000000',
  footerColor: '#232056',
};

const extractCrmToken = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const pathMatch = trimmed.match(/\/crm\/([^/?#]+)\/?$/i);
  if (pathMatch) {
    return pathMatch[1];
  }

  try {
    const pathname = new URL(trimmed).pathname;
    const urlPathMatch = pathname.match(/\/crm\/([^/]+)\/?$/i);
    if (urlPathMatch) {
      return urlPathMatch[1];
    }
  } catch (error) {
    // Fall through to the raw token value.
  }

  return trimmed;
};

type PortalTextField = {
  [K in keyof IPortalConfigurationForm]: IPortalConfigurationForm[K] extends string ? K : never;
}[keyof IPortalConfigurationForm];

interface IPortalConfigurationForm {
  portalName: string;
  contactUsUrl: string;
  facebookUrl: string;
  xUrl: string;
  instagramUrl: string;
  youtubeUrl: string;
  linkedinUrl: string;
  elevioWorkspaceName: string;
  elevioAccountId: string;
  elevioApiKey: string;
  elevioSecret: string;
}

interface IPortalConfigurationState {
  form: IPortalConfigurationForm;
  elevioApiKeyConfigured: boolean;
  elevioSecretConfigured: boolean;
  fieldErrors: Partial<Record<PortalFooterUrlField, string>>;
  formMessage: string | null;
}

export interface IPortalConfigurationProps extends RouteComponentProps<{ agency: string }> {
  agency: IAgencySettings;
  productConfigurations: IProductConfigurationsResponse;
  productConfigurationsAgencyId: number | null;
  portalConfiguration: ICitizenPortalConfigResponse;
  fetchAgencySettings: TFetchAgencySettings;
  fetchAgencyProductConfigs: TFetchAgencyProductConfigs;
  fetchCitizenPortalConfig: TFetchCitizenPortalConfig;
  saveCitizenPortalConfig: TSaveCitizenPortalConfig;
  isFetching: boolean;
}

export class PortalConfiguration extends React.Component<IPortalConfigurationProps, IPortalConfigurationState> {
  constructor(props: IPortalConfigurationProps) {
    super(props);

    this.state = this.buildStateFromProps(props);
  }

  getRouteAgencyId = (props: IPortalConfigurationProps = this.props): number | null => {
    const agencyId = Number(props.match.params.agency);

    return Number.isSafeInteger(agencyId) && agencyId > 0 ? agencyId : null;
  };

  getAgencySettingsForRoute = (props: IPortalConfigurationProps = this.props): IAgencySettings | null => {
    const routeAgencyId = this.getRouteAgencyId(props);

    if (!routeAgencyId || props.agency?.id !== routeAgencyId) {
      return null;
    }

    return props.agency;
  };

  getPortalConfigurationForRoute = (
    props: IPortalConfigurationProps = this.props
  ): ICitizenPortalConfigResponse | undefined => {
    const routeAgencyId = this.getRouteAgencyId(props);

    if (!routeAgencyId || props.portalConfiguration?.agencyId !== routeAgencyId) {
      return undefined;
    }

    return props.portalConfiguration;
  };

  getProductConfigurationsForRoute = (
    props: IPortalConfigurationProps = this.props
  ): IProductConfigurationsResponse | undefined => {
    const routeAgencyId = this.getRouteAgencyId(props);

    if (!routeAgencyId || props.productConfigurationsAgencyId !== routeAgencyId) {
      return undefined;
    }

    return props.productConfigurations ?? undefined;
  };

  buildStateFromProps = (props: IPortalConfigurationProps): IPortalConfigurationState => {
    const agencyName = this.getAgencySettingsForRoute(props)?.name ?? '';
    const config = this.getPortalConfigurationForRoute(props);

    return this.getStateFromConfig(config, agencyName);
  };

  syncFormFromProps = () => {
    this.setState(this.buildStateFromProps(this.props));
  };

  loadAgencyData = (agencyId: number) => {
    this.props.fetchAgencySettings(agencyId);
    this.props.fetchAgencyProductConfigs(agencyId);
    this.props.fetchCitizenPortalConfig(agencyId);
  };

  getDefaultForm = (agencyName = ''): IPortalConfigurationForm => ({
    portalName: agencyName,
    contactUsUrl: '',
    facebookUrl: '',
    xUrl: '',
    instagramUrl: '',
    youtubeUrl: '',
    linkedinUrl: '',
    elevioWorkspaceName: '',
    elevioAccountId: '',
    elevioApiKey: '',
    elevioSecret: '',
  });

  getStateFromConfig = (config?: ICitizenPortalConfigResponse, agencyName = ''): IPortalConfigurationState => {
    const form = this.getDefaultForm(agencyName);

    if (config) {
      form.portalName = config.portalName || agencyName || form.portalName;
      form.contactUsUrl = config.contactUsUrl || '';
      form.facebookUrl = config.facebookUrl || '';
      form.xUrl = config.xUrl || '';
      form.linkedinUrl = config.linkedinUrl || '';
      form.instagramUrl = config.instagramUrl || '';
      form.youtubeUrl = config.youtubeUrl || '';
      form.elevioWorkspaceName = config.elevioWorkspaceName || '';
      form.elevioAccountId = config.elevioAccountId || '';
      form.elevioApiKey = '';
      form.elevioSecret = '';
    }

    return {
      form,
      elevioApiKeyConfigured: Boolean(config?.elevioApiKeyConfigured),
      elevioSecretConfigured: Boolean(config?.elevioSecretConfigured || config?.elevioTokenConfigured),
      fieldErrors: {},
      formMessage: null,
    };
  };

  componentDidMount() {
    const routeAgencyId = this.getRouteAgencyId();

    if (routeAgencyId) {
      this.loadAgencyData(routeAgencyId);
    }
  }

  componentDidUpdate(prevProps: IPortalConfigurationProps) {
    const prevRouteAgencyId = this.getRouteAgencyId(prevProps);
    const routeAgencyId = this.getRouteAgencyId();

    if (prevRouteAgencyId !== routeAgencyId) {
      if (routeAgencyId) {
        this.loadAgencyData(routeAgencyId);
      }

      this.syncFormFromProps();

      return;
    }

    if (!routeAgencyId) {
      return;
    }

    const routeAgency = this.getAgencySettingsForRoute();
    const routeConfig = this.getPortalConfigurationForRoute();
    const prevRouteAgency = this.getAgencySettingsForRoute(prevProps);
    const prevRouteConfig = this.getPortalConfigurationForRoute(prevProps);

    if (routeAgency !== prevRouteAgency && routeConfig === prevRouteConfig) {
      const previousAgencyName = prevRouteAgency?.name ?? '';
      const agencyName = routeAgency?.name ?? '';

      this.setState((prevState) => ({
        form: {
          ...prevState.form,
          ...(prevState.form.portalName === previousAgencyName ? { portalName: agencyName } : {}),
        },
      }));
    } else if (routeConfig !== prevRouteConfig) {
      this.syncFormFromProps();
    }
  }

  handleFieldChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.currentTarget;

    this.setState((prevState) => {
      const fieldErrors = { ...prevState.fieldErrors };
      if (fieldErrors[name as PortalFooterUrlField]) {
        delete fieldErrors[name as PortalFooterUrlField];
      }

      return {
        form: {
          ...prevState.form,
          [name]: value,
        },
        fieldErrors,
        formMessage: null,
      };
    });
  };

  getAgencyCrmToken = (): string =>
    extractCrmToken(this.getProductConfigurationsForRoute()?.crm?.token || '') ||
    extractCrmToken(this.getPortalConfigurationForRoute()?.crmAppUrl || '');

  buildSavePayload = (): ICitizenPortalConfigRequest => {
    const { elevioApiKey, elevioSecret, ...editableFields } = this.state.form;
    const existing = this.getPortalConfigurationForRoute();

    const payload: ICitizenPortalConfigRequest = {
      ...editableFields,
      crmAppUrl: this.getAgencyCrmToken() || undefined,
    };

    if (elevioApiKey) {
      payload.elevioApiKey = elevioApiKey;
    }

    if (elevioSecret) {
      payload.elevioSecret = elevioSecret;
    }

    // Portal-wrapper-managed fields are omitted on update so Cyberdyne preserves
    // stored values without re-sending nulls that fail OpenAPI validation.
    if (!existing?.id) {
      payload.template = 'default';
      payload.darkNavigation = false;
      payload.siteBannerColor = DEFAULT_COLORS.siteBannerColor;
      payload.buttonColor = DEFAULT_COLORS.buttonColor;
      payload.embeddedFaqTitleColor = DEFAULT_COLORS.embeddedFaqTitleColor;
      payload.faqCategoryColor = DEFAULT_COLORS.faqCategoryColor;
      payload.faqTitleColor = DEFAULT_COLORS.faqTitleColor;
      payload.footerColor = DEFAULT_COLORS.footerColor;
    }

    return payload;
  };

  saveForm = () => {
    const agencyId = Number(this.props.match.params.agency);
    const payload = this.buildSavePayload();

    return this.props.saveCitizenPortalConfig(agencyId, payload).then((saveAction) => {
      if (saveAction.type === CITIZEN_PORTAL_CONFIG_SAVE_SUCCESS) {
        this.props.fetchCitizenPortalConfig(agencyId);
      }

      return saveAction;
    });
  };

  handleSave = (event: React.FormEvent) => {
    event.preventDefault();

    const fieldErrors = validatePortalFooterUrlFields(this.state.form);

    if (Object.keys(fieldErrors).length > 0) {
      this.setState({
        fieldErrors,
        formMessage: 'Nothing was saved. Fix the highlighted fields and try again.',
      });

      return undefined;
    }

    this.setState({ fieldErrors: {}, formMessage: null });

    return this.saveForm();
  };

  renderTextField = (
    name: PortalTextField,
    label: string,
    type = 'text',
    required = false,
    placeholder = '',
    hint = ''
  ) => {
    const error = this.state.fieldErrors[name as PortalFooterUrlField];

    return (
      <div className={`portal-configuration__field${error ? ' portal-configuration__field--invalid' : ''}`}>
        <label htmlFor={`portal-${name}`}>
          {label}
          {required && <span aria-hidden="true"> *</span>}
        </label>
        <input
          id={`portal-${name}`}
          name={name}
          type={type}
          value={this.state.form[name]}
          required={required}
          placeholder={placeholder}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `portal-${name}-error` : hint ? `portal-${name}-hint` : undefined}
          onChange={this.handleFieldChange}
        />
        {hint && !error ? (
          <small id={`portal-${name}-hint`} className="portal-configuration__hint">
            {hint}
          </small>
        ) : null}
        {error ? (
          <p id={`portal-${name}-error`} className="portal-configuration__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  };

  renderPortalNameSection = () => (
    <section className="portal-configuration__section" aria-labelledby="portal-name-heading">
      <h4 id="portal-name-heading">Portal Name</h4>
      <div className="portal-configuration__grid">
        {this.renderTextField('portalName', 'Portal display name', 'text', true, 'Example: Stockton Code Connect')}
      </div>
    </section>
  );

  renderFooterSection = () => (
    <section className="portal-configuration__section" aria-labelledby="portal-footer-heading">
      <h4 id="portal-footer-heading">Footer & Social Links</h4>
      <div className="portal-configuration__grid">
        {this.renderTextField(
          'contactUsUrl',
          'Contact Us URL',
          'url',
          false,
          'https://www.example.gov/contact',
          'Must start with http:// or https://'
        )}
        {this.renderTextField(
          'facebookUrl',
          'Facebook URL',
          'url',
          false,
          'https://www.facebook.com/your-page',
          'Must start with http:// or https://'
        )}
        {this.renderTextField(
          'xUrl',
          'X / Twitter URL',
          'url',
          false,
          'https://x.com/your-handle',
          'Must start with http:// or https://'
        )}
        {this.renderTextField(
          'instagramUrl',
          'Instagram URL',
          'url',
          false,
          'https://www.instagram.com/your-handle',
          'Must start with http:// or https://'
        )}
        {this.renderTextField(
          'youtubeUrl',
          'YouTube URL',
          'url',
          false,
          'https://www.youtube.com/@your-channel',
          'Must start with http:// or https://'
        )}
        {this.renderTextField(
          'linkedinUrl',
          'LinkedIn URL',
          'url',
          false,
          'https://www.linkedin.com/company/your-agency',
          'Must start with http:// or https://'
        )}
      </div>
    </section>
  );

  renderIntegrationSection = () => (
    <section className="portal-configuration__section" aria-labelledby="portal-integration-heading">
      <h4 id="portal-integration-heading">Elev.io Connection</h4>
      <div className="portal-configuration__grid">
        {this.renderTextField('elevioWorkspaceName', 'Elev.io workspace name')}
        {this.renderTextField('elevioAccountId', 'Elev.io account ID')}
        <div className="portal-configuration__field">
          <label htmlFor="portal-elevioApiKey">Elev.io API key</label>
          <input
            id="portal-elevioApiKey"
            name="elevioApiKey"
            type="password"
            value={this.state.form.elevioApiKey}
            autoComplete="new-password"
            placeholder={
              this.state.elevioApiKeyConfigured ? 'API key is already saved. Enter a new value to replace it.' : ''
            }
            onChange={this.handleFieldChange}
          />
          <small>The API key is encrypted at rest and is never returned after save.</small>
        </div>
        <div className="portal-configuration__field">
          <label htmlFor="portal-elevioSecret">Elev.io secret / JWT</label>
          <input
            id="portal-elevioSecret"
            name="elevioSecret"
            type="password"
            value={this.state.form.elevioSecret}
            autoComplete="new-password"
            placeholder={
              this.state.elevioSecretConfigured ? 'Secret is already saved. Enter a new value to replace it.' : ''
            }
            onChange={this.handleFieldChange}
          />
          <small>The secret is encrypted at rest and is never returned after save.</small>
        </div>
      </div>
    </section>
  );

  render() {
    const agencyId = Number(this.props.match.params.agency);
    const agencyName = this.getAgencySettingsForRoute()?.name;

    return (
      <div className="portal-configuration">
        <header className="portal-configuration__header">
          <Link to="/agencies">
            <span className="back-arrow" />
            Back to Agencies
          </Link>
          <h2>Configure Portal</h2>
        </header>
        <main className="portal-configuration__content">
          <div className="portal-configuration__title">
            <div>
              <h3>{agencyName || `Agency ${agencyId}`}</h3>
              <p>
                Set the portal name, footer links, and Elev.io connection here. Branding, colors, images, and live
                publish are managed in portal-wrapper.
              </p>
            </div>
            <span>Agency ID: {agencyId}</span>
          </div>
          <form onSubmit={this.handleSave} noValidate>
            {this.state.formMessage ? (
              <p className="portal-configuration__form-message" role="alert">
                {this.state.formMessage}
              </p>
            ) : null}
            {this.renderPortalNameSection()}
            {this.renderFooterSection()}
            {this.renderIntegrationSection()}
            <div className="portal-configuration__actions">
              <Button content="Save draft" onClick={this.handleSave} />
            </div>
          </form>
        </main>
      </div>
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  agency: state.agency.agencySettings,
  productConfigurations: state.agency.productConfigurations,
  productConfigurationsAgencyId: state.agency.productConfigurationsAgencyId,
  portalConfiguration: state.agency.portalConfiguration,
  isFetching: state.agency.isFetching,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchAgencySettings: (agencyId: number) => dispatch(fetchAgencySettings(agencyId)),
  fetchAgencyProductConfigs: (agencyId: number) => dispatch(fetchAgencyProductConfigs(agencyId)),
  fetchCitizenPortalConfig: (agencyId: number) => dispatch(fetchCitizenPortalConfig(agencyId)),
  saveCitizenPortalConfig: (agencyId: number, request: ICitizenPortalConfigRequest) =>
    dispatch(saveCitizenPortalConfig(agencyId, request)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(PortalConfiguration));
